import { Listbox, Transition } from '@headlessui/react';
import {
    ArrowPathIcon,
    CheckIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LoadingSpinner } from '../LoadingSpinner';
import { useEditor } from '../context/EditorContext/EditorContext';
import { useNotifications } from '../context/NotificationContext/NotificationContext';
import { API } from '../utils/api';

const RESTORE_POLL_INTERVAL_MS = 2500;
const RESTORE_TIMEOUT_MS = 10 * 60 * 1000;
const FAILED_DEPLOYMENT_STATES = new Set(['ERROR', 'CANCELED', 'CANCELLED']);

const wait = (duration) =>
    new Promise((resolve) => window.setTimeout(resolve, duration));

async function waitForRestoreDeployment(companyId, deploymentId, isCurrent) {
    const startedAt = Date.now();
    let consecutivePollFailures = 0;

    while (Date.now() - startedAt < RESTORE_TIMEOUT_MS) {
        await wait(RESTORE_POLL_INTERVAL_MS);
        if (!isCurrent()) return null;

        let deployment;
        try {
            deployment = await API.get(
                `${companyId}/publish/${encodeURIComponent(deploymentId)}`
            );
            consecutivePollFailures = 0;
        } catch (error) {
            consecutivePollFailures += 1;
            if (consecutivePollFailures >= 3) throw error;
            continue;
        }

        const status = String(deployment?.status || '').toUpperCase();
        if (status === 'READY') return deployment;
        if (FAILED_DEPLOYMENT_STATES.has(status)) {
            throw new Error(`Vercel deployment ${status.toLowerCase()}`);
        }
    }

    throw new Error('The restored site took too long to redeploy.');
}

function formatBackupDate(value) {
    if (!value) return 'Unknown date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown date';

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(date);
}

export function Backup() {
    const editor = useEditor();
    const notifications = useNotifications();
    const companyId = editor.global.data.company_id;
    const [backups, setBackups] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const restoreAttemptRef = useRef(0);

    const selectedBackup = useMemo(
        () => backups.find((backup) => backup.deploymentId === selectedId),
        [backups, selectedId]
    );

    const loadBackups = async () => {
        if (!companyId) return;
        setLoading(true);
        setLoadError(false);

        try {
            const response = await API.get(`${companyId}/backups`);
            const nextBackups = Array.isArray(response?.backups)
                ? response.backups
                : [];
            setBackups(nextBackups);
            setSelectedId((current) => {
                if (
                    current &&
                    nextBackups.some(
                        (backup) => backup.deploymentId === current
                    )
                ) {
                    return current;
                }
                return (
                    nextBackups.find((backup) => !backup.current)
                        ?.deploymentId ||
                    nextBackups[0]?.deploymentId ||
                    ''
                );
            });
        } catch {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBackups();
    }, [companyId]);

    useEffect(
        () => () => {
            restoreAttemptRef.current += 1;
        },
        []
    );

    const handleRestore = async () => {
        if (!selectedBackup || selectedBackup.current || restoring) return;

        const confirmed = window.confirm(
            `Restore the site to the preview from ${formatBackupDate(
                selectedBackup.createdAt
            )}? Your current version will remain available in the backup history.`
        );
        if (!confirmed) return;

        const restoreAttempt = restoreAttemptRef.current + 1;
        restoreAttemptRef.current = restoreAttempt;
        let reloading = false;
        setRestoring(true);
        try {
            const response = await API.post(`${companyId}/backups/restore`, {
                deploymentId: selectedBackup.deploymentId,
            });

            if (response?.alreadyCurrent) {
                notifications.push({
                    variant: 'success',
                    title: 'This backup is already the current version.',
                });
                await loadBackups();
                return;
            }

            if (!response?.deploymentId) {
                throw new Error(
                    'The backup was restored, but its deployment could not be tracked.'
                );
            }

            await waitForRestoreDeployment(
                companyId,
                response.deploymentId,
                () => restoreAttemptRef.current === restoreAttempt
            );

            if (restoreAttemptRef.current === restoreAttempt) {
                reloading = true;
                window.location.reload();
            }
        } catch (error) {
            notifications.push({
                variant: 'error',
                title:
                    error?.message ||
                    'The backup could not be restored and redeployed.',
            });
        } finally {
            if (restoreAttemptRef.current === restoreAttempt && !reloading) {
                setRestoring(false);
            }
        }
    };

    return (
        <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-sm">
            {loading ? (
                <div className="flex items-center gap-2 py-2 text-xs text-neutral-500">
                    <LoadingSpinner size="small" />
                    <span>Loading backups…</span>
                </div>
            ) : loadError ? (
                <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-inset ring-red-200">
                    <p>Backups could not be loaded.</p>
                    <button
                        type="button"
                        onClick={loadBackups}
                        className="mt-2 font-semibold underline"
                    >
                        Try again
                    </button>
                </div>
            ) : backups.length === 0 ? (
                <p className="py-2 text-xs text-neutral-500">
                    No completed preview backups are available yet.
                </p>
            ) : (
                <div className="space-y-3">
                    <div>
                        <span
                            id="site-backup-select-label"
                            className="mb-1 block text-[11px] font-medium leading-5 text-neutral-500"
                        >
                            Preview version
                        </span>
                        <Listbox value={selectedId} onChange={setSelectedId}>
                            <div className="relative w-full rounded-xl bg-neutral-300 p-[2px] focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                                <Listbox.Button
                                    id="site-backup-select"
                                    aria-labelledby="site-backup-select-label site-backup-select"
                                    className="relative flex h-9 w-full cursor-pointer items-center rounded-[10px] bg-white px-3 pr-9 text-left text-xs leading-5 text-neutral-800 focus:outline-none"
                                >
                                    <span className="truncate">
                                        {formatBackupDate(
                                            selectedBackup?.createdAt
                                        )}{' '}
                                        — {selectedBackup?.commitMessage}
                                        {selectedBackup?.current
                                            ? ' (Current)'
                                            : ''}
                                    </span>
                                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                        <ChevronDownIcon className="h-4 w-4 text-neutral-500" />
                                    </span>
                                </Listbox.Button>

                                <Transition
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100 scale-100"
                                    leaveTo="opacity-0 scale-95"
                                >
                                    <Listbox.Options className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none">
                                        {backups.map((backup) => (
                                            <Listbox.Option
                                                key={backup.deploymentId}
                                                value={backup.deploymentId}
                                                className={({
                                                    active,
                                                    selected,
                                                }) =>
                                                    [
                                                        'flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-2',
                                                        active
                                                            ? 'bg-primary-50 text-primary-700'
                                                            : 'text-neutral-800',
                                                        selected
                                                            ? 'font-medium'
                                                            : '',
                                                    ].join(' ')
                                                }
                                            >
                                                {({ selected }) => (
                                                    <>
                                                        <span className="truncate">
                                                            {formatBackupDate(
                                                                backup.createdAt
                                                            )}{' '}
                                                            —{' '}
                                                            {
                                                                backup.commitMessage
                                                            }
                                                            {backup.current
                                                                ? ' (Current)'
                                                                : ''}
                                                        </span>
                                                        {selected ? (
                                                            <CheckIcon className="h-4 w-4 shrink-0 text-primary-600" />
                                                        ) : null}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>
                    </div>

                    {selectedBackup ? (
                        <div className="rounded-lg bg-neutral-50 px-3 py-2 text-[11px] leading-4 text-neutral-500">
                            <p className="truncate font-medium text-neutral-700">
                                {selectedBackup.commitMessage}
                            </p>
                            <p>Commit {selectedBackup.commitSha.slice(0, 7)}</p>
                            {selectedBackup.url ? (
                                <a
                                    href={selectedBackup.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-1 inline-block font-semibold text-primary-700 hover:text-primary-800"
                                >
                                    Open this preview
                                </a>
                            ) : null}
                        </div>
                    ) : null}

                    <button
                        type="button"
                        onClick={handleRestore}
                        disabled={
                            restoring ||
                            !selectedBackup ||
                            selectedBackup.current
                        }
                        className="inline-flex items-center gap-2 rounded-md bg-primary-700 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {restoring ? (
                            <LoadingSpinner size="small" color="#FFFFFF" />
                        ) : (
                            <i
                                className="fas fa-clock-rotate-left"
                                aria-hidden="true"
                            />
                        )}
                        <span>
                            {restoring ? 'Restoring…' : 'Restore this backup'}
                        </span>
                    </button>
                </div>
            )}
            {restoring && typeof document !== 'undefined'
                ? createPortal(
                      <div
                          className="fixed inset-0 z-[30000] flex cursor-wait items-center justify-center bg-neutral-950/60 px-6 backdrop-blur-sm"
                          role="status"
                          aria-live="polite"
                          aria-label="Restoring backup"
                      >
                          <div className="flex max-w-sm flex-col items-center rounded-2xl border border-white/15 bg-neutral-900/90 px-8 py-7 text-center text-white shadow-2xl">
                              <ArrowPathIcon
                                  className="h-12 w-12 animate-spin text-white"
                                  style={{ animationDirection: 'reverse' }}
                                  aria-hidden="true"
                              />
                              <p className="mt-4 text-base font-semibold">
                                  Restoring your backup
                              </p>
                              <p className="mt-2 text-sm leading-5 text-neutral-300">
                                  Please keep this page open. It will refresh
                                  automatically as soon as your restored site is
                                  ready.
                              </p>
                          </div>
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
}
